/**
 * G012 — 人口与劳动力。
 *
 * 设计取舍见 docs/goals/G012-design-notes.md。决定本文件形状的一条：
 *
 * **指派只有一份权威记录**（`assignments: citizenId -> JobRef`）。
 * 「这栋楼有谁在上班」是从它**派生**的查询，不是第二份存储。
 * 三条验收（不超槽位 / 一人不占两岗 / 不可工作不占岗）因此都成为结构性质，
 * 而不是「要记得两边同步」的约定 —— 漏同步是必然会发生的。
 *
 * 数值一律整数：效率因子用 0–10000 basis points，浮点连乘对顺序敏感（同 G009/G010）。
 *
 * ⚠ **本文件不得从 `kernel.js` 取值**。kernel 要 import 本模块的
 * `createEmptyPopulationState`，反向再取值就形成循环引用 —— 模块级常量会在
 * 初始化期落进 TDZ。
 *
 * **单测抓不住它**：vitest 直接解析 `src` 的 TS，加载的不是编译产物，
 * 那条循环在源码图里被 TS 的类型擦除绕过了。真正会炸的是消费 `dist` 的一切：
 * 浏览器白屏、`sim:smoke` 直接 ReferenceError。
 * 判据不是「Node 还是浏览器」，而是**「src 还是 dist」**。
 * 班次窗口等常量一律在本文件内自持。
 */

import { cellKey, type BuildCellId } from '../spatial/types.js';
import type { CommandHandler, SimulationSystem } from '../system.js';
import type { SimulationContext } from '../context.js';

/* ===================== 基本类型 ===================== */

/** 技能领域。与建筑的 `requiredSkills` 对齐。 */
export type SkillId = 'mining' | 'engineering' | 'life_support' | 'logistics' | 'science';
export const SKILL_IDS: readonly SkillId[] = [
  'mining', 'engineering', 'life_support', 'logistics', 'science',
];

/** 三班制。班次决定「此刻是否在岗」，不决定「是否占槽」——见设计笔记 §4。 */
export type ShiftId = 'day' | 'swing' | 'night';
export const SHIFT_IDS: readonly ShiftId[] = ['day', 'swing', 'night'];

/** 每个班次覆盖的游戏内分钟区间（左闭右开，整数）。 */
export const SHIFT_WINDOWS: Readonly<Record<ShiftId, readonly [number, number]>> = {
  day: [360, 840],      // 06:00–14:00
  swing: [840, 1320],   // 14:00–22:00
  night: [1320, 360],   // 22:00–06:00（跨日）
};

/**
 * 居民状态。
 *
 * `dead` 与 `incapacitated` 都属于**不可工作**；分开是因为前者不可逆、后者可恢复，
 * G013 的健康系统需要区分。本 Goal 只用「能不能上岗」这一个语义。
 */
export type CitizenStatus = 'active' | 'resting' | 'incapacitated' | 'dead';

/** 可上岗的状态集合。判定集中在此，避免各处散写 `!== 'dead'` 而漏掉伤残。 */
export function canWork(status: CitizenStatus): boolean {
  return status === 'active';
}

export interface Citizen {
  readonly id: string;
  /** 显示名。由播种时的种子 RNG 生成，不含真实人名。 */
  readonly name: string;
  status: CitizenStatus;
  /** 各技能等级 0–100。缺省视为 0。 */
  skills: Partial<Record<SkillId, number>>;
  /** 家庭组 id。同组居民倾向同班次（自动器的软偏好，不是硬约束）。 */
  readonly householdId: string;
  /** 当前班次。 */
  shift: ShiftId;
  /** 健康 0–10000 bp。**G012 只给接口**，波动由 G013 负责。 */
  health: number;
  /** 疲劳 0–10000 bp（越高越累）。同上。 */
  fatigue: number;
}

/** 一个岗位引用：哪栋楼的第几个槽位。 */
export interface JobRef {
  readonly buildingId: string;
  /** 槽位序号 0..workerSlots-1。 */
  readonly slot: number;
  /** 玩家手动指派的岗位会锁定，自动器不得覆盖（验收第 2 条）。 */
  locked: boolean;
}

/* ===================== 有效劳动力分解 ===================== */

/** 满值。所有因子都是 0–10000 bp。 */
export const BP_FULL = 10000;

/**
 * 有效劳动力的**分解式**（需求第 3 条）。
 *
 * 每个因子单独存下来，UI 展开时直接读 —— 若只存最终值，UI 只能反推，
 * 而反推必然会在某个边界上和模拟层对不上（与 G010 储备小时数同一条规则）。
 */
export interface EffectivenessBreakdown {
  /** 技能匹配度：岗位要求 vs 居民实际。 */
  readonly skill: number;
  /** 健康。G013 接口。 */
  readonly health: number;
  /** 环境（氧气/污染/温度）。G013 接口。 */
  readonly environment: number;
  /** 疲劳。G013 接口。 */
  readonly fatigue: number;
  /** 设施状态（建筑是否受损/限电）。G018 接口。 */
  readonly facility: number;
  /** 逐项相乘后的最终值，0–10000 bp。 */
  readonly total: number;
}

/** 按 bp 连乘。整数运算，逐步取整，顺序无关。 */
export function combineBp(...factors: readonly number[]): number {
  let acc = BP_FULL;
  for (const f of factors) acc = Math.floor((acc * clampBp(f)) / BP_FULL);
  return acc;
}

function clampBp(v: number): number {
  if (!Number.isFinite(v) || v < 0) return 0;
  return v > BP_FULL ? BP_FULL : Math.floor(v);
}

/* ===================== 世界状态 ===================== */

export interface PopulationState {
  /** 居民表。 */
  citizens: Record<string, Citizen>;
  /** 稳定遍历顺序（与 EntityStore.order 同理由）。 */
  order: string[];
  /**
   * 指派的**唯一权威记录**：citizenId -> 岗位。
   * 建筑侧的「谁在这上班」由 `staffOf()` 派生，不另存一份。
   */
  assignments: Record<string, JobRef>;
  /** 上一次结算的效率分解，按 citizenId。UI 展开用。 */
  effectiveness: Record<string, EffectivenessBreakdown>;
  /** 每栋楼当前在岗（考虑班次与状态）的人数，派生缓存，供 G011 读取。 */
  staffedCount: Record<string, number>;
  /** 环境因子（全殖民地一致，G013 接管后按区域细分）。 */
  environmentBp: number;
}

export function createEmptyPopulationState(): PopulationState {
  return {
    citizens: {}, order: [], assignments: {},
    effectiveness: {}, staffedCount: {}, environmentBp: BP_FULL,
  };
}

export const POPULATION_DOMAIN = 'population';
interface WorldWithPopulation { [POPULATION_DOMAIN]: PopulationState }
function popOf(ctx: SimulationContext): PopulationState {
  return (ctx.state as unknown as WorldWithPopulation)[POPULATION_DOMAIN];
}

/* ===================== 派生查询 ===================== */

/**
 * 某栋楼的在册员工（**派生**，非存储）。
 * 返回按 citizenId 字典序，保证遍历确定性。
 */
export function staffOf(pop: PopulationState, buildingId: string): string[] {
  const out: string[] = [];
  for (const cid of Object.keys(pop.assignments).sort()) {
    if (pop.assignments[cid]!.buildingId === buildingId) out.push(cid);
  }
  return out;
}

/** 某名居民当前的岗位；未指派为 null。 */
export function jobOf(pop: PopulationState, citizenId: string): JobRef | null {
  return pop.assignments[citizenId] ?? null;
}

/** 此刻该班次是否在岗。夜班跨日，故用「区间包含」而不是简单比较。 */
export function isOnShift(shift: ShiftId, minuteOfDay: number): boolean {
  const [from, to] = SHIFT_WINDOWS[shift];
  return from <= to ? minuteOfDay >= from && minuteOfDay < to
    : minuteOfDay >= from || minuteOfDay < to;
}

/* ===================== 建筑投影（依赖注入） ===================== */

/** 人口所需的最小建筑定义投影。 */
export interface JobSpec {
  readonly id: string;
  readonly workerSlots: number;
  /** 岗位要求的技能等级 0–100。空表示无要求。 */
  readonly requiredSkills: Readonly<Partial<Record<SkillId, number>>>;
}
export type GetJobSpec = (defId: string) => JobSpec | undefined;

/* ===================== 稳定拒绝码 ===================== */

export type PopulationRejection =
  | 'CITIZEN_NOT_FOUND'
  | 'CITIZEN_CANNOT_WORK'
  | 'BUILDING_NOT_FOUND'
  | 'BUILDING_NOT_OPERATIONAL'
  | 'JOB_SLOT_INVALID'
  | 'JOB_SLOT_TAKEN'
  | 'JOB_NO_SLOTS'
  | 'ALREADY_ASSIGNED_HERE'
  | 'NOT_ASSIGNED'
  | 'SHIFT_INVALID';

/* ===================== 命令 ===================== */

export const ASSIGN_JOB = 'assign_job';
export const UNASSIGN_JOB = 'unassign_job';
export const SET_SHIFT = 'set_shift';

export interface AssignJobPayload {
  readonly citizenId: string;
  readonly buildingId: string;
  /** 省略时取第一个空槽。 */
  readonly slot?: number;
}
export interface UnassignJobPayload { readonly citizenId: string }
export interface SetShiftPayload { readonly citizenId: string; readonly shift: ShiftId }

interface BuildingLike {
  readonly defId: string;
  readonly anchor: BuildCellId;
  readonly lifecycle: string;
}

function buildingOf(ctx: SimulationContext, id: string): BuildingLike | undefined {
  return ctx.state.entities.components['building']?.[id] as BuildingLike | undefined;
}

const reject = (code: PopulationRejection): { accepted: false; rejectionCode: string } =>
  ({ accepted: false, rejectionCode: code });
const accept = { accepted: true } as const;

/**
 * 指派命令。
 *
 * 这里对「不可工作」拒一次，结算层每 Tick 还会过滤一次 —— **不是重复**：
 * 命令层给玩家即时反馈，结算层兜住「指派之后才生病/死亡」这种时序。
 */
export function createAssignJobHandler(getSpec: GetJobSpec): CommandHandler<AssignJobPayload> {
  return {
    type: ASSIGN_JOB,
    apply(ctx, payload) {
      const pop = popOf(ctx);
      const c = pop.citizens[payload.citizenId];
      if (!c) return reject('CITIZEN_NOT_FOUND');
      if (!canWork(c.status)) return reject('CITIZEN_CANNOT_WORK');

      const b = buildingOf(ctx, payload.buildingId);
      if (!b) return reject('BUILDING_NOT_FOUND');
      if (b.lifecycle !== 'operational') return reject('BUILDING_NOT_OPERATIONAL');

      const spec = getSpec(b.defId);
      const slots = spec?.workerSlots ?? 0;
      if (slots <= 0) return reject('JOB_NO_SLOTS');

      const staff = staffOf(pop, payload.buildingId);
      const taken = new Set(staff.map((cid) => pop.assignments[cid]!.slot));

      let slot = payload.slot;
      if (slot === undefined) {
        slot = -1;
        for (let i = 0; i < slots; i++) if (!taken.has(i)) { slot = i; break; }
        if (slot < 0) return reject('JOB_SLOT_TAKEN');
      } else {
        if (!Number.isInteger(slot) || slot < 0 || slot >= slots) return reject('JOB_SLOT_INVALID');
        // 已被别人占；被自己占则是幂等重复指派
        const holder = staff.find((cid) => pop.assignments[cid]!.slot === slot);
        if (holder !== undefined && holder !== payload.citizenId) return reject('JOB_SLOT_TAKEN');
        if (holder === payload.citizenId) return reject('ALREADY_ASSIGNED_HERE');
      }

      /* 一人一岗由 Record 的键唯一性保证：写入即自动解除旧岗位，
         不需要「先 unassign 再 assign」，也就不存在忘记解除的可能。 */
      pop.assignments[payload.citizenId] = {
        buildingId: payload.buildingId, slot, locked: true,   // 手动指派即表达意图
      };
      return accept;
    },
  };
}

export function createUnassignJobHandler(): CommandHandler<UnassignJobPayload> {
  return {
    type: UNASSIGN_JOB,
    apply(ctx, payload) {
      const pop = popOf(ctx);
      if (!pop.citizens[payload.citizenId]) return reject('CITIZEN_NOT_FOUND');
      if (!pop.assignments[payload.citizenId]) return reject('NOT_ASSIGNED');
      delete pop.assignments[payload.citizenId];
      return accept;
    },
  };
}

export function createSetShiftHandler(): CommandHandler<SetShiftPayload> {
  return {
    type: SET_SHIFT,
    apply(ctx, payload) {
      const pop = popOf(ctx);
      const c = pop.citizens[payload.citizenId];
      if (!c) return reject('CITIZEN_NOT_FOUND');
      if (!SHIFT_IDS.includes(payload.shift)) return reject('SHIFT_INVALID');
      c.shift = payload.shift;
      return accept;
    },
  };
}

/* ===================== 自动分配器 ===================== */

/**
 * 自动建议分配（需求第 1 条）。
 *
 * 三条硬性质，都有测试：
 * 1. **不动锁定岗位**，也不把人从锁定岗位上抢走（验收第 2 条）
 * 2. **幂等**：同样输入跑两次结果相同
 * 3. **确定性**：候选人按 (技能匹配降序, citizenId 升序) 排，岗位按 cellKey 升序
 *    —— 不用插入顺序（G009 报告第 8 节的教训）
 */
export function autoAssign(
  pop: PopulationState,
  entities: { readonly order: readonly string[]; readonly components: Record<string, Record<string, unknown>> },
  getSpec: GetJobSpec,
): void {
  // 未锁定的指派先释放，锁定的原样保留
  for (const cid of Object.keys(pop.assignments)) {
    if (!pop.assignments[cid]!.locked) delete pop.assignments[cid];
  }

  const jobs: Array<{ buildingId: string; spec: JobSpec; key: string }> = [];
  for (const id of entities.order) {
    const b = entities.components['building']?.[id] as BuildingLike | undefined;
    if (!b || b.lifecycle !== 'operational') continue;
    const spec = getSpec(b.defId);
    if (!spec || spec.workerSlots <= 0) continue;
    jobs.push({ buildingId: id, spec, key: cellKey(b.anchor) });
  }
  jobs.sort((x, y) => (x.key < y.key ? -1 : x.key > y.key ? 1 : 0));

  const free = pop.order
    .filter((cid) => canWork(pop.citizens[cid]!.status) && !pop.assignments[cid])
    .sort();

  for (const job of jobs) {
    const existing = staffOf(pop, job.buildingId);
    const taken = new Set(existing.map((cid) => pop.assignments[cid]!.slot));
    const openSlots = job.spec.workerSlots - taken.size;
    if (openSlots <= 0) continue;

    /* **必须按班次成组配人**，否则一栋 3 槽的楼会拿到分属三个班次的 3 个人 ——
       任何时刻都只有 1 人在岗，`staffedCount` 永远配不齐，产线永远报 NO_WORKERS。
       这不是理论问题：第一版就是这么写的，实机 48 人全部就业却 0 栋楼开工。

       班次选择：优先跟随已锁定同事的班次（玩家的手动安排优先），
       否则选**当前自由人里人数最多**的班次；同数按 SHIFT_IDS 顺序，保证确定性。 */
    const lockedShift = existing.length > 0
      ? pop.citizens[existing[0]!]!.shift : undefined;
    const targetShift = lockedShift ?? pickRichestShift(pop, free);

    // 该班次的自由人，按技能匹配降序、id 升序
    const pool = free
      .filter((cid) => pop.citizens[cid]!.shift === targetShift)
      .sort((x, y) => {
        const d = skillMatchBp(pop.citizens[y]!, job.spec) - skillMatchBp(pop.citizens[x]!, job.spec);
        return d !== 0 ? d : (x < y ? -1 : 1);
      });
    // 同班次人数不足以配齐整栋楼时，**一个都不派** —— 与 G011 的
    // 「要么配齐要么不配」同一条规则：半个班组产不出东西，占着人还不如空着。
    if (pool.length < openSlots) continue;

    let picked = 0;
    for (let slot = 0; slot < job.spec.workerSlots && picked < openSlots; slot++) {
      if (taken.has(slot)) continue;
      const cid = pool[picked++]!;
      free.splice(free.indexOf(cid), 1);
      pop.assignments[cid] = { buildingId: job.buildingId, slot, locked: false };
    }
  }
}

/** 自由人里人数最多的班次。同数按 SHIFT_IDS 顺序，保证确定性。 */
function pickRichestShift(pop: PopulationState, free: readonly string[]): ShiftId {
  const tally: Record<ShiftId, number> = { day: 0, swing: 0, night: 0 };
  for (const cid of free) tally[pop.citizens[cid]!.shift]++;
  let best: ShiftId = SHIFT_IDS[0]!;
  for (const s of SHIFT_IDS) if (tally[s] > tally[best]) best = s;
  return best;
}

/** 技能匹配度 0–10000 bp。无要求视为满分。 */
export function skillMatchBp(c: Citizen, spec: JobSpec): number {
  const reqs = Object.keys(spec.requiredSkills).sort() as SkillId[];
  if (reqs.length === 0) return BP_FULL;
  let acc = 0;
  for (const s of reqs) {
    const need = spec.requiredSkills[s] ?? 0;
    if (need <= 0) { acc += BP_FULL; continue; }
    const have = c.skills[s] ?? 0;
    acc += Math.min(BP_FULL, Math.floor((have * BP_FULL) / need));
  }
  return Math.floor(acc / reqs.length);
}

/* ===================== 系统 ===================== */

export interface PopulationSystemOptions {
  readonly getSpec: GetJobSpec;
  /** 设施状态因子（建筑受损/限电）。G018 接管前恒为满值。 */
  readonly facilityBp?: (buildingId: string) => number;
}

/**
 * 人口系统。每 Tick 三步：
 *   1. 清理失效指派（建筑没了 / 居民不可工作）
 *   2. 计算每人的效率分解
 *   3. 汇总每栋楼的在岗人数，供 G011 生产系统读取
 *
 * 必须排在生产系统**之前**：生产要读本 Tick 的在岗人数。
 */
export function createPopulationSystem(opts: PopulationSystemOptions): SimulationSystem {
  const { getSpec, facilityBp } = opts;

  return {
    id: 'population',
    update(ctx: SimulationContext): void {
      const pop = popOf(ctx);
      const minute = ctx.state.clock.minuteOfDay;

      /* --- 1. 清理失效指派 --- */
      for (const cid of Object.keys(pop.assignments).sort()) {
        const job = pop.assignments[cid]!;
        const c = pop.citizens[cid];
        const b = buildingOf(ctx, job.buildingId);
        // 结算层兜住「指派之后才发生」的状态变化 —— 命令层拒绝挡不住时序
        if (!c || !canWork(c.status) || !b || b.lifecycle !== 'operational') {
          delete pop.assignments[cid];
        }
      }

      /* --- 2. 效率分解 --- */
      pop.effectiveness = {};
      for (const cid of pop.order) {
        const c = pop.citizens[cid];
        if (!c) continue;
        const job = pop.assignments[cid];
        const spec = job ? specForBuilding(ctx, job.buildingId, getSpec) : undefined;
        const skill = spec ? skillMatchBp(c, spec) : BP_FULL;
        // 疲劳越高效率越低：10000 疲劳 = 0 效率
        const fatigueFactor = BP_FULL - clampBp(c.fatigue);
        const facility = job && facilityBp ? facilityBp(job.buildingId) : BP_FULL;
        pop.effectiveness[cid] = {
          skill,
          health: clampBp(c.health),
          environment: clampBp(pop.environmentBp),
          fatigue: fatigueFactor,
          facility: clampBp(facility),
          total: combineBp(skill, c.health, pop.environmentBp, fatigueFactor, facility),
        };
      }

      /* --- 3. 在岗人数汇总（供 G011） --- */
      const staffed: Record<string, number> = {};
      for (const cid of Object.keys(pop.assignments).sort()) {
        const job = pop.assignments[cid]!;
        const c = pop.citizens[cid]!;
        if (!isOnShift(c.shift, minute)) continue;     // 占槽是全天的，在岗看班次
        staffed[job.buildingId] = (staffed[job.buildingId] ?? 0) + 1;
      }
      pop.staffedCount = staffed;
    },
  };
}

function specForBuilding(
  ctx: SimulationContext, buildingId: string, getSpec: GetJobSpec,
): JobSpec | undefined {
  const b = buildingOf(ctx, buildingId);
  return b ? getSpec(b.defId) : undefined;
}

/* ===================== 播种 ===================== */

/** 名字构件。刻意是编号化的殖民代号，不用真实人名。 */
const GIVEN = ['凛', '澈', '穹', '砺', '樾', '砚', '苓', '珩', '沨', '翎', '岫', '棠'];
const HOUSE = ['CZ-A', 'CZ-B', 'CZ-C', 'CZ-D', 'CZ-E', 'CZ-F'];

/**
 * 播种初始人口。**必须传入种子 RNG**（`AGENTS.md` 禁止 `Math.random` 决定模拟）。
 * 返回生成的居民 id 列表。
 */
export function seedPopulation(
  pop: PopulationState,
  count: number,
  nextRandom: () => number,
): string[] {
  const ids: string[] = [];
  for (let i = 0; i < count; i++) {
    const id = `cit_${String(i).padStart(4, '0')}`;
    const skills: Partial<Record<SkillId, number>> = {};
    // 每人两项主技能，其余为基础值 —— 让技能匹配真的有区分度
    const primary = SKILL_IDS[Math.floor(nextRandom() * SKILL_IDS.length)]!;
    const second = SKILL_IDS[Math.floor(nextRandom() * SKILL_IDS.length)]!;
    for (const s of SKILL_IDS) skills[s] = 20 + Math.floor(nextRandom() * 20);
    skills[primary] = 60 + Math.floor(nextRandom() * 40);
    skills[second] = Math.max(skills[second] ?? 0, 45 + Math.floor(nextRandom() * 30));
    const citizen: Citizen = {
      id,
      name: `${GIVEN[Math.floor(nextRandom() * GIVEN.length)]}-${String(i + 1).padStart(3, '0')}`,
      status: 'active',
      skills,
      householdId: HOUSE[Math.floor(nextRandom() * HOUSE.length)]!,
      shift: SHIFT_IDS[i % SHIFT_IDS.length]!,      // 三班均摊，不随机
      health: BP_FULL,
      fatigue: 0,
    };
    pop.citizens[id] = citizen;
    pop.order.push(id);
    ids.push(id);
  }
  return ids;
}

/* ===================== 投影（供 UI 与测试） ===================== */

export interface CitizenView {
  readonly id: string;
  readonly name: string;
  readonly status: CitizenStatus;
  readonly shift: ShiftId;
  readonly householdId: string;
  readonly skills: Partial<Record<SkillId, number>>;
  readonly job: { buildingId: string; slot: number; locked: boolean } | null;
  readonly effectiveness: EffectivenessBreakdown | null;
  readonly onShift: boolean;
}

/** 人口列表投影（UI 用），按 citizenId 升序。 */
export function citizenList(pop: PopulationState, minuteOfDay: number): CitizenView[] {
  return [...pop.order].sort().map((cid) => {
    const c = pop.citizens[cid]!;
    const j = pop.assignments[cid];
    return {
      id: c.id, name: c.name, status: c.status, shift: c.shift,
      householdId: c.householdId, skills: { ...c.skills },
      job: j ? { buildingId: j.buildingId, slot: j.slot, locked: j.locked } : null,
      effectiveness: pop.effectiveness[cid] ?? null,
      onShift: isOnShift(c.shift, minuteOfDay),
    };
  });
}

/** 某栋楼的岗位面板投影（UI 用，与人口列表互相定位 —— 需求第 4 条）。 */
export function jobPanel(
  pop: PopulationState, buildingId: string, slots: number, minuteOfDay: number,
): Array<{ slot: number; citizenId: string | null; name: string | null; locked: boolean; onShift: boolean; effectiveness: number | null }> {
  const staff = staffOf(pop, buildingId);
  const bySlot = new Map<number, string>();
  for (const cid of staff) bySlot.set(pop.assignments[cid]!.slot, cid);
  const out = [];
  for (let slot = 0; slot < slots; slot++) {
    const cid = bySlot.get(slot);
    const c = cid ? pop.citizens[cid] : undefined;
    out.push({
      slot,
      citizenId: cid ?? null,
      name: c?.name ?? null,
      locked: cid ? pop.assignments[cid]!.locked : false,
      onShift: c ? isOnShift(c.shift, minuteOfDay) : false,
      effectiveness: cid ? (pop.effectiveness[cid]?.total ?? null) : null,
    });
  }
  return out;
}

/** 殖民地人口概览。 */
export function populationSummary(pop: PopulationState, minuteOfDay: number): {
  total: number; active: number; incapacitated: number; dead: number;
  employed: number; onShiftNow: number; byShift: Record<ShiftId, number>;
} {
  let active = 0, incapacitated = 0, dead = 0, onShiftNow = 0;
  const byShift: Record<ShiftId, number> = { day: 0, swing: 0, night: 0 };
  for (const cid of pop.order) {
    const c = pop.citizens[cid]!;
    if (c.status === 'dead') dead++;
    else if (c.status === 'incapacitated') incapacitated++;
    else if (c.status === 'active') active++;
    byShift[c.shift]++;
    if (canWork(c.status) && isOnShift(c.shift, minuteOfDay)) onShiftNow++;
  }
  return {
    total: pop.order.length, active, incapacitated, dead,
    employed: Object.keys(pop.assignments).length,
    onShiftNow, byShift,
  };
}
